/* search-engine.js — Beach AI custom search engine.
 *
 * Replaces Shopify's native /search?q= with a client-side scored search
 * over a pre-built JSON index (search-index.json). Built because:
 *   - Shopify search is keyword-AND with no ranking
 *   - "headphones" doesn't match a product titled "WH-1000XM6"
 *   - "soundbar" doesn't match a product titled "HW-Q990F"
 *   - "77 inch tv" wants 77" first then other sizes, not none
 *
 * Architecture:
 *   1. On page load, fetch /assets/search-index.json (cached in
 *      localStorage with version key for invalidation).
 *   2. Read ?q= from URL.
 *   3. Tokenize, expand synonyms, score every product, rank, render.
 *   4. Replace the section's data-bsr-list contents with rendered cards.
 *
 * Scales to ~10K products comfortably (sub-100ms scoring).
 * Above 10K, swap to server-side or Algolia.
 */
(function () {
  'use strict';

  var INDEX_URL = window.BSR_INDEX_URL || '/cdn/shop/t/0/assets/search-index.json';
  var SYNONYM_URL = window.BSR_SYNONYM_URL || '/cdn/shop/t/0/assets/search-synonyms.json';
  var BRAND_URL = window.BSR_BRAND_URL || '/cdn/shop/t/0/assets/brand-assets.json';
  var LS_KEY = 'beachai-search-index-v2';

  /* ── Field weights — higher = bigger contribution to score.
     Tuned so title and vendor dominate, with keyword/spec hints filling
     the long tail. */
  var FIELD_WEIGHTS = {
    titleExact: 100,
    titleAll:    40,
    titleAny:    15,
    vendorAny:   25,
    typeAny:     12,
    tagAny:      18,
    specAny:     14,
    keywordAny:  10,
    descAny:      4,
    fuzzy:        3,
  };

  var indexPromise = null;
  var synonymPromise = null;
  var brandPromise = null;

  /* ── Brand asset loader (logos + YouTube IDs per vendor) ─── */
  function loadBrands() {
    if (brandPromise) return brandPromise;
    brandPromise = fetch(BRAND_URL, { cache: 'force-cache' })
      .then(function (r) { return r.ok ? r.json() : { brands: {} }; })
      .then(function (j) {
        // Build a flat lookup: every key + every alias points to the same record.
        var byKey = {};
        var brands = j.brands || {};
        Object.keys(brands).forEach(function (k) {
          var b = brands[k];
          if (!b) return;
          var rec = Object.assign({ _key: k }, b);
          byKey[k.toLowerCase()] = rec;
          (b.aliases || []).forEach(function (a) {
            byKey[String(a).toLowerCase()] = rec;
          });
          if (b.name) byKey[String(b.name).toLowerCase()] = rec;
        });
        return { brands: brands, byKey: byKey };
      })
      .catch(function () { return { brands: {}, byKey: {} }; });
    return brandPromise;
  }

  /* Category detection — maps query tokens to a brand-asset category.
     Order in CAT_RULES matters: more specific categories first. */
  var CAT_RULES = [
    { key: 'soundbar',  tokens: ['soundbar','soundbars','sound-bar'] },
    { key: 'headphone', tokens: ['headphone','headphones','earbud','earbuds','airpods','headset','iems'] },
    { key: 'monitor',   tokens: ['monitor','monitors','display','displays','ultragear','odyssey'] },
    { key: 'tv',        tokens: ['tv','tvs','television','televisions','oled','qled','uled','bravia','neo'] },
    { key: 'camera',    tokens: ['camera','cameras','dslr','mirrorless','lens','lenses','alpha','lumix','eos','nikkor','rebel'] },
    { key: 'projector', tokens: ['projector','projectors'] },
    { key: 'audio',     tokens: ['audio','speaker','speakers','sound'] },
  ];

  function detectCategory(originalTokens, topProduct) {
    var tokSet = {};
    originalTokens.forEach(function (t) { tokSet[t] = 1; });

    // 1) Look for category-keyword tokens in the query
    for (var i = 0; i < CAT_RULES.length; i++) {
      var rule = CAT_RULES[i];
      for (var j = 0; j < rule.tokens.length; j++) {
        if (tokSet[rule.tokens[j]]) return rule.key;
      }
    }
    // 2) Fall back to the top product's product_category metafield
    if (topProduct && topProduct.sp) {
      var pc = String(topProduct.sp['specs.product_category'] || '').toLowerCase().trim();
      if (pc) {
        // normalize a few synonyms
        if (pc.indexOf('television') >= 0) return 'tv';
        if (pc.indexOf('headphone') >= 0)  return 'headphone';
        if (pc.indexOf('soundbar') >= 0)   return 'soundbar';
        if (pc.indexOf('monitor') >= 0)    return 'monitor';
        if (pc.indexOf('camera') >= 0)     return 'camera';
        return pc;
      }
    }
    return null;
  }

  /* Pick a brand record + the category-specific video.
     CASCADE — banner ALWAYS shows. Tries hardest matches first:
       1. Brand keyword in the query itself ("sony tv" → Sony)
       2. Top main result's vendor ("65 inch tv" → top hit is Samsung S95F → Samsung)
       3. Any main result's vendor matches a configured brand
       4. Brand whose categories include the detected query category (TV/headphones/etc.)
          — random pick among matching brands so the banner feels fresh each load.
       5. Any brand with a default video (last-ditch fallback).
     Returns { brand, video, category }. */
  function pickBrand(originalTokens, brandData, mainProducts) {
    if (!brandData || !brandData.byKey) return null;
    var allBrands = brandData.brands || {};
    var allBrandKeys = Object.keys(allBrands);
    if (allBrandKeys.length === 0) return null;

    var brand = null;
    var topProduct = (mainProducts && mainProducts[0]) || null;
    var cat = detectCategory(originalTokens, topProduct);

    // 1. Direct token match
    for (var i = 0; i < originalTokens.length; i++) {
      var hit = brandData.byKey[originalTokens[i]];
      if (hit) { brand = hit; break; }
    }

    // 2. Top result's vendor
    if (!brand && topProduct && topProduct.v) {
      var v0 = String(topProduct.v).toLowerCase();
      brand = brandData.byKey[v0] || null;
    }

    // 3. Any result's vendor (in rank order)
    if (!brand && mainProducts && mainProducts.length > 1) {
      for (var pi = 1; pi < mainProducts.length; pi++) {
        var pv = (mainProducts[pi] && mainProducts[pi].v) ? String(mainProducts[pi].v).toLowerCase() : '';
        if (pv && brandData.byKey[pv]) { brand = brandData.byKey[pv]; break; }
      }
    }

    // 4. Category-aware random pick among brands that have a video for this category
    if (!brand && cat) {
      var matchKeys = [];
      for (var k = 0; k < allBrandKeys.length; k++) {
        var b = allBrands[allBrandKeys[k]];
        if (b && b.categories && b.categories[cat] && b.categories[cat].videoId) {
          matchKeys.push(allBrandKeys[k]);
        }
      }
      if (matchKeys.length > 0) {
        var rk = matchKeys[Math.floor(Math.random() * matchKeys.length)];
        brand = brandData.byKey[rk.toLowerCase()];
      }
    }

    // 5. Last-ditch: any brand with a default video
    if (!brand) {
      var fallbackKeys = [];
      for (var m = 0; m < allBrandKeys.length; m++) {
        var b2 = allBrands[allBrandKeys[m]];
        if (b2 && b2['default'] && b2['default'].videoId) {
          fallbackKeys.push(allBrandKeys[m]);
        }
      }
      if (fallbackKeys.length > 0) {
        var fk = fallbackKeys[Math.floor(Math.random() * fallbackKeys.length)];
        brand = brandData.byKey[fk.toLowerCase()];
      }
    }

    if (!brand) return null;

    // Pick the right video based on category, fall back gracefully
    var cats = brand.categories || {};
    var video = (cat && cats[cat]) ? cats[cat] : (brand['default'] || null);
    if (!video || !video.videoId) {
      var ck = Object.keys(cats);
      for (var c = 0; c < ck.length; c++) {
        if (cats[ck[c]] && cats[ck[c]].videoId) { video = cats[ck[c]]; break; }
      }
    }

    return { brand: brand, video: video, category: cat };
  }

  function renderBrandPanel(picked, query) {
    var panel = document.querySelector('[data-bsr-brand]');
    var heroBrandCol = document.querySelector('[data-bsr-hero-brand]');
    var heroLogo = document.querySelector('[data-bsr-hero-logo]');
    if (!panel) return;

    if (!picked || !picked.brand) {
      panel.setAttribute('hidden', '');
      if (heroBrandCol) heroBrandCol.setAttribute('hidden', '');
      return;
    }

    var brand = picked.brand;
    var video = picked.video || {};

    var videoEl = panel.querySelector('[data-bsr-brand-video]');
    var tagEl   = panel.querySelector('[data-bsr-brand-tagline]');

    if (heroLogo) {
      heroLogo.src = brand.logo || '';
      heroLogo.alt = (brand.name || '') + ' logo';
    }
    if (heroBrandCol) heroBrandCol.removeAttribute('hidden');
    if (tagEl) tagEl.textContent = video.tagline || '';

    if (videoEl) {
      var vid = video.videoId || '';
      if (vid) {
        // Cinematic ambient loop — no YouTube chrome. Params:
        //   controls=0      hide play/pause/scrubber
        //   disablekb=1     no keyboard controls
        //   fs=0            no fullscreen button
        //   iv_load_policy=3  no annotations
        //   cc_load_policy=0  captions off
        //   modestbranding/rel reduce branding + related-videos at end
        //   loop+playlist  seamless infinite loop of the same video
        //   start=4        skip past YouTube's intro/title-card + controls fade-in
        // A poster image covers the iframe during the autoplay handoff
        // so YouTube's preview UI never shows. It fades out after ~1.4s.
        var src =
          'https://www.youtube.com/embed/' + encodeURIComponent(vid) +
          '?autoplay=1&mute=1&playsinline=1&controls=0&disablekb=1&fs=0' +
          '&modestbranding=1&rel=0&iv_load_policy=3&cc_load_policy=0' +
          '&start=4&loop=1&playlist=' + encodeURIComponent(vid);
        var poster = 'https://i.ytimg.com/vi/' + vid + '/maxresdefault.jpg';
        videoEl.innerHTML =
          '<iframe src="' + src + '" ' +
          'title="' + escapeHtml((brand.name || '') + ' video') + '" ' +
          'allow="autoplay; encrypted-media; picture-in-picture" ' +
          'loading="lazy"></iframe>' +
          '<img class="bsr__brand-poster" src="' + escapeHtml(poster) + '" alt="" aria-hidden="true">' +
          '<div class="bsr__brand-edgemask bsr__brand-edgemask--top" aria-hidden="true"></div>' +
          '<div class="bsr__brand-edgemask bsr__brand-edgemask--bot" aria-hidden="true"></div>' +
          '<div class="bsr__brand-clickshield" aria-hidden="true"></div>';
        // Fade out the poster after autoplay has had time to take over.
        var posterEl = videoEl.querySelector('.bsr__brand-poster');
        if (posterEl) {
          setTimeout(function () {
            posterEl.style.opacity = '0';
            // Remove from layout after the transition so it doesn't catch clicks.
            setTimeout(function () { posterEl.parentNode && posterEl.parentNode.removeChild(posterEl); }, 700);
          }, 1400);
        }
      } else {
        videoEl.innerHTML = '';
        videoEl.style.background = brand.color || '#0b0d12';
      }
    }

    // Subtle accent border + glow in the brand color
    if (brand.color) {
      panel.style.borderColor = 'color-mix(in srgb, ' + brand.color + ' 25%, transparent)';
      panel.style.setProperty('--brand-glow', 'color-mix(in srgb, ' + brand.color + ' 22%, transparent)');
    }
    panel.removeAttribute('hidden');
  }

  /* ── Brand-strip marquee renderer — populates the row of all brand
        logos under the hero. Each brand is duplicated so the CSS
        translate loop is seamless. */
  function renderBrandStrip(brandData) {
    var rail = document.querySelector('[data-bsr-brand-strip-rail]');
    if (!rail || !brandData || !brandData.brands) return;

    var brands = brandData.brands;
    var keys = Object.keys(brands);
    if (keys.length === 0) {
      // No brands? Hide the whole strip rather than show an empty bar.
      var strip = document.querySelector('[data-bsr-brand-strip]');
      if (strip) strip.style.display = 'none';
      return;
    }

    function logoLink(key, b) {
      var name = b.name || key;
      return '<a class="bsr__brand-strip-item" ' +
              'href="/search?q=' + encodeURIComponent(key) + '" ' +
              'aria-label="' + escapeHtml(name) + '" ' +
              'title="' + escapeHtml(name) + '">' +
              '<img src="' + escapeHtml(b.logo || '') + '" alt="' + escapeHtml(name) + '" loading="lazy">' +
            '</a>';
    }

    // Render the list TWICE for seamless infinite scroll
    var html = keys.map(function (k) { return logoLink(k, brands[k]); }).join('');
    rail.innerHTML = html + html;
  }

  /* ── Synonym dictionary loader ────────────────────────────── */
  function loadSynonyms() {
    if (synonymPromise) return synonymPromise;
    synonymPromise = fetch(SYNONYM_URL, { cache: 'force-cache' })
      .then(function (r) { return r.ok ? r.json() : { groups: [] }; })
      .then(function (j) {
        var index = {};
        (j.groups || []).forEach(function (group) {
          var norm = group.map(function (t) { return normalize(t); });
          norm.forEach(function (k) { if (k) index[k] = norm; });
        });
        return { groups: j.groups || [], index: index };
      })
      .catch(function () { return { groups: [], index: {} }; });
    return synonymPromise;
  }

  /* ── Index loader: localStorage-cached with version key ───── */
  function loadIndex() {
    if (indexPromise) return indexPromise;

    indexPromise = fetch(INDEX_URL, { cache: 'force-cache' })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then(function (data) {
        try {
          var cached = localStorage.getItem(LS_KEY);
          if (cached) {
            var parsed = JSON.parse(cached);
            if (parsed.version === data.version) return parsed;
          }
          localStorage.setItem(LS_KEY, JSON.stringify(data));
        } catch (e) { /* localStorage full or disabled — fine */ }
        return data;
      })
      .catch(function (err) {
        console.warn('[search] index load failed:', err);
        // Last-ditch: return cached even if stale
        try {
          var cached = localStorage.getItem(LS_KEY);
          if (cached) return JSON.parse(cached);
        } catch (e) {}
        return { products: [], version: 0 };
      });

    return indexPromise;
  }

  /* ── Tokenization + normalization ─────────────────────────── */
  function normalize(s) {
    return String(s || '')
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, ' ')   // strip punctuation
      .replace(/\s+/g, ' ')
      .trim();
  }

  function tokenize(s) {
    var n = normalize(s);
    if (!n) return [];
    return n.split(' ').filter(function (t) {
      // Drop noise words and very short tokens (1 char)
      if (t.length < 2) return false;
      if (NOISE.indexOf(t) >= 0) return false;
      return true;
    });
  }

  // Common English stopwords + commerce noise that hurts ranking.
  var NOISE = ('a an and or the of is for in on with to from at by '
    + 'best new buy online shop store').split(' ');

  /* ── Fuzzy matching (typo tolerance) ────────────────────────
     Levenshtein distance over a precomputed vocabulary built from
     every product's title / vendor / type / tags. Query tokens that
     don't hit the index exactly get fuzzy-expanded to vocab tokens
     within edit-distance budget; "samusng" snaps to "samsung" with
     dist=1, "iphome" snaps to "iphone" with dist=1, etc. */
  function levenshtein(a, b) {
    if (a === b) return 0;
    var la = a.length, lb = b.length;
    if (!la) return lb;
    if (!lb) return la;
    if (Math.abs(la - lb) > 4) return 99;  // bail early on big size diffs
    var dp = new Array(lb + 1);
    for (var j = 0; j <= lb; j++) dp[j] = j;
    for (var i = 1; i <= la; i++) {
      var prev = dp[0];
      dp[0] = i;
      for (var j = 1; j <= lb; j++) {
        var temp = dp[j];
        if (a.charCodeAt(i - 1) === b.charCodeAt(j - 1)) {
          dp[j] = prev;
        } else {
          dp[j] = 1 + Math.min(prev, dp[j - 1], dp[j]);
        }
        prev = temp;
      }
    }
    return dp[lb];
  }

  /* Tighter budget for short tokens (avoid "tv" → "tx"/"tw" noise). */
  function fuzzyBudget(token) {
    if (token.length <= 3) return 0;        // exact only
    if (token.length <= 5) return 1;        // 1 typo allowed
    return 2;                               // 2 typos allowed for longer tokens
  }

  var _vocab = null;       // Array<string> of all index tokens
  var _vocabBuilt = false;

  function buildVocab(products) {
    if (_vocabBuilt) return _vocab;
    var seen = Object.create(null);
    function add(t) { if (t && t.length >= 3) seen[t] = 1; }
    (products || []).forEach(function (p) {
      tokenize(p.t || '').forEach(add);
      tokenize(p.v || '').forEach(add);
      tokenize(p.ty || '').forEach(add);
      (p.tg || []).forEach(function (tag) { tokenize(tag).forEach(add); });
      var kw = (p.sp && p.sp['custom.search_keywords']) || '';
      tokenize(kw).forEach(add);
    });
    _vocab = Object.keys(seen);
    _vocabBuilt = true;
    return _vocab;
  }

  /* For a single query token, return all vocab tokens within fuzzy budget. */
  function fuzzyMatches(token, vocab) {
    var budget = fuzzyBudget(token);
    if (budget === 0) return [];
    var out = [];
    var tlen = token.length;
    for (var i = 0; i < vocab.length; i++) {
      var v = vocab[i];
      if (v === token) continue;
      if (Math.abs(v.length - tlen) > budget) continue;
      if (levenshtein(token, v) <= budget) out.push(v);
    }
    return out;
  }

  /* Reconstruct a "did you mean" string for a zero-result query.
     For each original token, pick the closest vocab match (lowest dist).
     Returns null if no token has a fuzzy correction. */
  function didYouMean(query, products) {
    var tokens = tokenize(query);
    if (!tokens.length) return null;
    var vocab = buildVocab(products);
    var corrected = [];
    var anyCorrected = false;
    tokens.forEach(function (tok) {
      var budget = fuzzyBudget(tok);
      if (budget === 0) { corrected.push(tok); return; }
      var bestDist = budget + 1;
      var bestTok = tok;
      for (var i = 0; i < vocab.length; i++) {
        var v = vocab[i];
        if (Math.abs(v.length - tok.length) > budget) continue;
        var d = levenshtein(tok, v);
        if (d > 0 && d < bestDist) { bestDist = d; bestTok = v; }
      }
      if (bestTok !== tok) anyCorrected = true;
      corrected.push(bestTok);
    });
    return anyCorrected ? corrected.join(' ') : null;
  }

  /* ── Synonym + fuzzy expansion ─────────────────────────────
     Returns map: token → weight, where:
       1.0  = original query token
       0.7  = synonym
       0.5  = fuzzy match (typo correction) */
  function expandTokens(tokens, syn, vocab) {
    var out = {};
    tokens.forEach(function (t) { out[t] = 1.0; });

    // Look up each token AND every 2-word phrase in the synonym index.
    function pull(key) {
      var group = syn.index[key];
      if (!group) return;
      group.forEach(function (term) {
        normalize(term).split(' ').forEach(function (sub) {
          if (sub.length < 2 || NOISE.indexOf(sub) >= 0) return;
          if (!(sub in out)) out[sub] = 0.7;
        });
      });
    }
    tokens.forEach(pull);
    for (var i = 0; i < tokens.length - 1; i++) {
      pull(tokens[i] + ' ' + tokens[i + 1]);
    }
    if (tokens.length >= 2) pull(tokens.join(' '));

    // Fuzzy expansion — only for tokens that didn't get exact synonym hits
    // and only when the vocab is built (post-index-load).
    if (vocab && vocab.length) {
      tokens.forEach(function (tok) {
        var fuzzy = fuzzyMatches(tok, vocab);
        fuzzy.forEach(function (f) {
          if (!(f in out) || out[f] < 0.5) out[f] = 0.5;
        });
      });
    }

    return out;
  }

  /* ── Scoring ──────────────────────────────────────────────────
     Returns { score, strong } where:
       • score = weighted relevance score across all field hits
       • strong = true iff at least one ORIGINAL (non-synonym) token
                  hit a "strong" field (title / vendor / type / tags /
                  keywords / specs). Description-only or synonym-only
                  matches → strong=false → demoted to "Related products".
  */
  var STRONG_FIELDS = ['title', 'vendor', 'type', 'tags', 'specs', 'keywords'];

  function scoreProduct(p, weightedTokens, originalTokens) {
    var allTokens = Object.keys(weightedTokens);
    if (allTokens.length === 0) return { score: 0, strong: false };

    var titleN = normalize(p.t || '');
    var vendorN = normalize(p.v || '');
    var typeN = normalize(p.ty || '');
    var tagsN = normalize((p.tg || []).join(' '));
    var specsN = normalize(Object.values(p.sp || {}).join(' '));
    var keywords = ((p.sp && p.sp['custom.search_keywords']) || '');
    var keywordsN = normalize(keywords);
    var searchTextN = p.s || '';

    var score = 0;
    var matchedAny = false;
    var strong = false;     // ← any original token hit a strong field?
    var origSet = {};
    originalTokens.forEach(function (t) { origSet[t] = 1; });

    // 1. Whole-query exact title match (e.g. "samsung s95f" → strong)
    var origPhrase = originalTokens.join(' ');
    if (origPhrase && titleN.indexOf(origPhrase) >= 0) {
      score += FIELD_WEIGHTS.titleExact;
      matchedAny = true;
      strong = true;
    }

    // 2. Per-token field hits (weighted by token-importance × field weight)
    allTokens.forEach(function (tok) {
      var tw = weightedTokens[tok];
      var isOriginal = origSet[tok] === 1;
      var hit = false;
      var hitStrongField = false;

      if (titleN.indexOf(tok) >= 0) {
        score += FIELD_WEIGHTS.titleAny * tw;
        hit = true; hitStrongField = true;
      }
      if (vendorN.indexOf(tok) >= 0) {
        score += FIELD_WEIGHTS.vendorAny * tw;
        hit = true; hitStrongField = true;
      }
      if (typeN.indexOf(tok) >= 0) {
        score += FIELD_WEIGHTS.typeAny * tw;
        hit = true; hitStrongField = true;
      }
      if (tagsN.indexOf(tok) >= 0) {
        score += FIELD_WEIGHTS.tagAny * tw;
        hit = true; hitStrongField = true;
      }
      if (specsN.indexOf(tok) >= 0) {
        score += FIELD_WEIGHTS.specAny * tw;
        hit = true; hitStrongField = true;
      }
      if (keywordsN.indexOf(tok) >= 0) {
        score += FIELD_WEIGHTS.keywordAny * tw;
        hit = true; hitStrongField = true;
      }
      // Description is a weak signal — only count if NO other field hit.
      if (!hit && searchTextN.indexOf(tok) >= 0) {
        score += FIELD_WEIGHTS.descAny * tw;
        hit = true;
      }

      if (hit) matchedAny = true;
      // "Strong" requires an ORIGINAL token (not synonym) AND a strong field.
      if (hitStrongField && isOriginal) strong = true;
    });

    // 3. All ORIGINAL (non-synonym) tokens in title → bonus
    var allOriginalInTitle = originalTokens.every(function (t) { return titleN.indexOf(t) >= 0; });
    if (allOriginalInTitle && originalTokens.length > 0) {
      score += FIELD_WEIGHTS.titleAll;
      strong = true;
    }

    // 4. Boost in-stock products slightly so out-of-stock sink
    if (p.av) score *= 1.0; else score *= 0.85;

    // 5. Light rating boost (4.5★ catalog → tiny multiplier)
    score *= (1 + (p.r || 0) * 0.02);

    return { score: matchedAny ? score : 0, strong: strong };
  }

  /* ── Public search API ──────────────────────────────────────
     Returns { main: [products], related: [products], query: str }.
       • main    = strong matches (original tokens hit a strong field)
       • related = weak matches (synonym-only or description-only)
                   capped at MAX_RELATED so they don't dominate.
  */
  var MAX_RELATED = 8;
  var WEAK_FLOOR = 6;          // drop noise below this score

  function search(query, opts) {
    return Promise.all([loadIndex(), loadSynonyms(), loadBrands()]).then(function (vals) {
      var data = vals[0], syn = vals[1], brandData = vals[2];
      var products = data.products || [];
      var vocab = buildVocab(products);

      var origTokens = tokenize(query);
      if (origTokens.length === 0) {
        var sorted = products.slice().sort(function (a, b) { return (b.r || 0) - (a.r || 0); });
        var b0 = pickBrand([], brandData, sorted);
        return { main: sorted, related: [], query: query, brand: b0, suggestion: null };
      }

      var weighted = expandTokens(origTokens, syn, vocab);

      var strong = [], weak = [];
      for (var i = 0; i < products.length; i++) {
        var p = products[i];
        var r = scoreProduct(p, weighted, origTokens);
        if (r.score <= 0) continue;
        if (r.strong) {
          strong.push({ p: p, s: r.score });
        } else if (r.score >= WEAK_FLOOR) {
          weak.push({ p: p, s: r.score });
        }
      }
      strong.sort(function (a, b) { return b.s - a.s; });
      weak.sort(function (a, b) { return b.s - a.s; });

      var mainProducts = strong.map(function (x) { return x.p; });
      var brand = pickBrand(origTokens, brandData, mainProducts);

      // If no main hits, compute a "did you mean" suggestion against vocab.
      var suggestion = null;
      if (mainProducts.length === 0) {
        suggestion = didYouMean(query, products);
      }

      return {
        main:       mainProducts,
        related:    weak.slice(0, MAX_RELATED).map(function (x) { return x.p; }),
        query:      query,
        brand:      brand,
        suggestion: suggestion,
        topSellers: products.slice().sort(function (a, b) {
          // popularity-ish: rating × in-stock — gives us a stable "popular" rail
          return ((b.r || 0) * (b.av ? 1 : 0.5)) - ((a.r || 0) * (a.av ? 1 : 0.5));
        }).slice(0, 6),
      };
    });
  }

  /* Lightweight autocomplete: top N matches plus brand/category hints.
     Used by the header search bar — debounced, no full render. */
  function autocomplete(query, limit) {
    return Promise.all([loadIndex(), loadSynonyms(), loadBrands()]).then(function (vals) {
      var data = vals[0], syn = vals[1], brandData = vals[2];
      var products = data.products || [];
      var vocab = buildVocab(products);
      var origTokens = tokenize(query);
      if (origTokens.length === 0) return { products: [], brands: [], categories: [] };

      var weighted = expandTokens(origTokens, syn, vocab);
      var hits = [];
      for (var i = 0; i < products.length; i++) {
        var r = scoreProduct(products[i], weighted, origTokens);
        if (r.score > 0) hits.push({ p: products[i], s: r.score, strong: r.strong });
      }
      hits.sort(function (a, b) {
        if (a.strong && !b.strong) return -1;
        if (!a.strong && b.strong) return 1;
        return b.s - a.s;
      });

      // Surface brand suggestions: any vendor that matches a query token (exact or fuzzy).
      var brandSet = {};
      hits.slice(0, 12).forEach(function (h) {
        var v = h.p.v;
        if (v) brandSet[v] = (brandSet[v] || 0) + h.s;
      });
      var brandList = Object.keys(brandSet)
        .sort(function (a, b) { return brandSet[b] - brandSet[a]; })
        .slice(0, 3);

      // Category suggestions: pull dominant categories from top hits
      var catSet = {};
      hits.slice(0, 8).forEach(function (h) {
        var c = (h.p.sp && h.p.sp['specs.product_category']) || h.p.ty || '';
        if (c) catSet[c] = (catSet[c] || 0) + 1;
      });
      var catList = Object.keys(catSet)
        .sort(function (a, b) { return catSet[b] - catSet[a]; })
        .slice(0, 2);

      return {
        products: hits.slice(0, limit || 5).map(function (h) { return h.p; }),
        brands: brandList,
        categories: catList,
      };
    });
  }

  /* ── Render results into the section ──────────────────────── */
  function moneyFormat(cents) {
    var d = (cents / 100).toFixed(2);
    var parts = d.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return '$' + parts.join('.');
  }

  function escapeHtml(s) {
    return String(s || '').replace(/[&<>"']/g, function (c) {
      return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c];
    });
  }

  /* ── Title highlighting ───────────────────────────────────────
     Wraps matched query tokens (and their fuzzy/synonym expansions)
     in <mark> within product titles. Module-level _highlightTokens
     gets set by renderResults() before iterating cards. */
  var _highlightTokens = [];
  function highlightTitle(title) {
    var safe = escapeHtml(title || '');
    if (!_highlightTokens.length) return safe;
    var pattern = _highlightTokens
      .map(function (t) { return t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); })
      .filter(function (t) { return t.length >= 2; })
      .sort(function (a, b) { return b.length - a.length; })   // longer first
      .join('|');
    if (!pattern) return safe;
    try {
      var re = new RegExp('(' + pattern + ')', 'gi');
      return safe.replace(re, '<mark class="bsr__hl">$1</mark>');
    } catch (e) {
      return safe;
    }
  }

  /* ── Natural-language query parsing ───────────────────────────
     Pull out price modifiers and brand tokens from the user's query.
     "tv under 2000" → { clean: "tv", priceMax: 2000, interpretation: "TV under $2,000" }
     "samsung 4k oled" → { clean: "4k oled", vendor: "Samsung", interpretation: "Samsung TVs · 4K · OLED" }
     The cleaned query goes back to the search engine; the extracted filters
     get auto-applied via the existing filter UI on the results page. */
  var BRAND_LIST = ['samsung','sony','lg','panasonic','bose','sonos','dyson',
    'jbl','sennheiser','vizio','tcl','hisense','bowers','denon','yamaha',
    'klipsch','marshall','beats','apple','google','garmin','dji','gopro',
    'canon','nikon','fujifilm','leica','olympus','pentax','sigma','tamron',
    'kitchenaid','breville','vitamix','ninja','cuisinart','keurig','nespresso'];

  function parseNaturalQuery(query) {
    var out = {
      raw: query,
      clean: query,
      priceMin: null,
      priceMax: null,
      vendor: null,
      interpretation: null,
    };
    if (!query) return out;
    var q = ' ' + query.toLowerCase().trim() + ' ';

    // "under $X" / "below X" / "less than X"
    var m;
    if ((m = q.match(/\b(?:under|below|less than|cheaper than|<)\s*\$?\s*(\d{2,5})\b/))) {
      out.priceMax = parseInt(m[1], 10);
      q = q.replace(m[0], ' ');
    }
    // "over $X" / "above X" / "more than X"
    if ((m = q.match(/\b(?:over|above|more than|>)\s*\$?\s*(\d{2,5})\b/))) {
      out.priceMin = parseInt(m[1], 10);
      q = q.replace(m[0], ' ');
    }
    // "between $X and $Y" / "X to Y"
    if ((m = q.match(/\b(?:between\s*)?\$?\s*(\d{2,5})\s*(?:to|-|–)\s*\$?\s*(\d{2,5})\b/))) {
      out.priceMin = parseInt(m[1], 10);
      out.priceMax = parseInt(m[2], 10);
      q = q.replace(m[0], ' ');
    }

    // Brand detection — pick the first brand token that appears
    for (var i = 0; i < BRAND_LIST.length; i++) {
      var b = BRAND_LIST[i];
      var re = new RegExp('\\b' + b + '\\b', 'i');
      if (re.test(q)) {
        out.vendor = b.charAt(0).toUpperCase() + b.slice(1);
        // Don't strip the brand from the query — let the engine score it as a token.
        break;
      }
    }

    out.clean = q.replace(/\s+/g, ' ').trim() || query;

    // Build a human-readable interpretation only if we extracted something
    var bits = [];
    if (out.vendor) bits.push(out.vendor);
    if (out.clean && out.clean.toLowerCase() !== query.toLowerCase()) {
      bits.push(out.clean);
    }
    if (out.priceMin && out.priceMax) bits.push('$' + out.priceMin + '–$' + out.priceMax);
    else if (out.priceMax) bits.push('under $' + out.priceMax);
    else if (out.priceMin) bits.push('over $' + out.priceMin);
    if (bits.length && (out.priceMin || out.priceMax || out.vendor)) {
      out.interpretation = bits.join(' · ');
    }
    return out;
  }
  // Expose for page-level filter wiring
  window.BeachAIParse = parseNaturalQuery;

  // Pick 4 specs to render based on category — mirrors PDP/section logic
  var SPEC_TEMPLATES = {
    tv:        [['tvs.resolution','Resolution',''], ['tvs.refresh_rate','Refresh',' Hz'], ['tvs.hdr','HDR',''], ['tvs.smart_os','Platform','']],
    monitor:   [['specs.refresh_rate','Refresh',' Hz'], ['specs.resolution','Resolution',''], ['specs.panel_type','Panel',''], ['specs.screen_size','Size','']],
    headphone: [['specs.anc_type','ANC',''], ['specs.battery_life','Battery',''], ['specs.connectivity','Connect',''], ['specs.form_factor','Type','']],
    soundbar:  [['specs.audio_channels','Channels',''], ['specs.dolby_atmos','Atmos',''], ['specs.subwoofer_type','Sub',''], ['specs.hdmi_arc','HDMI','']],
    home_theater: [['specs.audio_channels','Channels',''], ['specs.dolby_atmos','Atmos',''], ['specs.subwoofer_type','Sub',''], ['specs.hdmi_arc','HDMI','']],
    camera:    [['specs.megapixels','Megapixels',''], ['specs.sensor_size','Sensor',''], ['specs.max_video','Max Video',''], ['specs.lens_mount','Mount','']],
    projector: [['specs.brightness','Brightness',''], ['specs.resolution','Resolution',''], ['specs.throw_type','Throw',''], ['specs.light_source','Light','']],
  };

  function categoryFor(p) {
    var cat = (p.sp && p.sp['specs.product_category']) || '';
    if (cat) return cat.toLowerCase();
    var ty = (p.ty || '').toLowerCase();
    if (ty.indexOf('television') >= 0) return 'tv';
    if (ty.indexOf('monitor') >= 0) return 'monitor';
    if (ty.indexOf('headphone') >= 0) return 'headphone';
    if (ty.indexOf('soundbar') >= 0) return 'soundbar';
    if (ty.indexOf('camera') >= 0) return 'camera';
    if (ty.indexOf('projector') >= 0) return 'projector';
    return '';
  }

  function renderCard(p, opts) {
    var isTop = !!(opts && opts.top);
    var cat = categoryFor(p);
    var tmpl = SPEC_TEMPLATES[cat] || SPEC_TEMPLATES.tv;
    var specsHtml = tmpl.map(function (t) {
      var key = t[0], label = t[1], suffix = t[2];
      var val = (p.sp || {})[key];
      if (!val) return '<div class="bsr__card-spec bsr__card-spec--empty"></div>';
      var fullText = label + ': ' + val + suffix;
      return (
        '<div class="bsr__card-spec" title="' + escapeHtml(fullText) + '">' +
          '<div class="bsr__card-spec-lbl">' + escapeHtml(label) + '</div>' +
          '<div class="bsr__card-spec-val">' + escapeHtml(val + suffix) + '</div>' +
        '</div>'
      );
    }).join('');

    var save = (p.cp || 0) > (p.p || 0) ? Math.round(((p.cp - p.p) / p.cp) * 100) : 0;

    var ratingHtml = '';
    if (p.r) {
      var stars = '';
      for (var i = 1; i <= 5; i++) {
        stars += i <= p.r ? '★' : '<span class="bsr__star-d">★</span>';
      }
      ratingHtml = (
        '<div class="bsr__card-rate">' +
          '<span class="bsr__rate-stars" aria-hidden="true">' + stars + '</span>' +
          '<span class="bsr__rate-num">' + p.r + '</span>' +
          (p.rc ? '<span class="bsr__rate-count">(' + p.rc + ')</span>' : '') +
        '</div>'
      );
    }

    var img = p.img ? '<img src="' + escapeHtml(p.img) + '" alt="" loading="lazy" width="200" height="200">' : '';
    var saletag = save > 0 ? '<span class="bsr__card-saletag">−' + save + '%</span>' : '';
    var compareHtml = (p.cp || 0) > (p.p || 0)
      ? '<span class="bsr__compare">List <s>' + moneyFormat(p.cp) + '</s></span>'
      : '';

    var topBadge = isTop
      ? '<span class="bsr__toppick-badge" aria-label="Beach AI Top Pick">' +
          '<svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">' +
            '<path d="M12 2l2.39 6.96H22l-6.18 4.49L18.18 22 12 17.27 5.82 22l2.36-8.55L2 8.96h7.61z"/>' +
          '</svg>' +
          'Top pick' +
        '</span>'
      : '';

    var classes = 'bsr__card' + (isTop ? ' bsr__card--top' : '');
    // Size pills — for TVs/monitors with size variants (or sibling
    // products grouped by base title). Each pill links to either:
    //   • /products/{handle}?variant={vid}  (variant within same product)
    //   • /products/{sibling-handle}        (separate product per size)
    // Whichever the index produced.
    var sizesHtml = '';
    if (p.sz && p.sz.length > 1) {
      sizesHtml =
        '<div class="bsr__card-sizes" aria-label="Available sizes">' +
          '<span class="bsr__card-sizes-lbl">Size</span>' +
          p.sz.map(function (s) {
            var href;
            // Anchor → browser jumps to + we add a focus pulse on the
            // PDP size selector so the user sees what they just selected.
            var anchor = '#bsr-size';
            if (s.h) {
              href = '/products/' + escapeHtml(s.h) + anchor;
            } else if (s.vid) {
              href = '/products/' + escapeHtml(p.h) + '?variant=' + escapeHtml(s.vid) + anchor;
            } else {
              href = '/products/' + escapeHtml(p.h) + anchor;
            }
            var isCurrent = !!(s.h && s.h === p.h);
            var cls = 'bsr__card-size' + (isCurrent ? ' is-current' : '');
            var label = (s.n || s.label || '') + '"';
            return '<a class="' + cls + '" href="' + href + '" ' +
                   (s.av === false ? 'aria-disabled="true" tabindex="-1" ' : '') +
                   'title="' + escapeHtml(label) + '">' + escapeHtml(label) + '</a>';
          }).join('') +
        '</div>';
    }

    // Add-to-cart button. Disabled if we don't have a variant ID in the
    // index (older indices) or the product is unavailable.
    var atcDisabled = !p.vid || !p.av;
    var atcHtml =
      '<button type="button" class="bsr__card-atc" ' +
        'data-bsr-atc ' +
        (p.vid ? 'data-variant="' + escapeHtml(String(p.vid)) + '" ' : '') +
        (atcDisabled ? 'disabled ' : '') +
        'aria-label="Add ' + escapeHtml(p.t) + ' to cart">' +
        '<svg class="bsr__card-atc-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
          '<circle cx="9" cy="20" r="1.4"/><circle cx="18" cy="20" r="1.4"/>' +
          '<path d="M3 4h2.5l2.4 11.5a2 2 0 0 0 2 1.5h7.6a2 2 0 0 0 2-1.5L21 8H6"/>' +
        '</svg>' +
        '<span class="bsr__card-atc-label">' +
          (atcDisabled ? (p.av ? 'Unavailable' : 'Out of stock') : 'Add to cart') +
        '</span>' +
      '</button>';

    return (
      '<article class="' + classes + '" ' +
        'data-vendor="' + escapeHtml(p.v) + '" ' +
        'data-price="' + (p.p / 100) + '" ' +
        'data-title="' + escapeHtml(normalize(p.t)) + '" ' +
        'data-rating="' + (p.r || 0) + '">' +
        topBadge +
        '<a class="bsr__card-img" href="/products/' + escapeHtml(p.h) + '" tabindex="-1" aria-hidden="true">' +
          img + saletag +
        '</a>' +
        '<div class="bsr__card-main">' +
          '<a class="bsr__card-title" href="/products/' + escapeHtml(p.h) + '">' + highlightTitle(p.t) + '</a>' +
          ratingHtml +
          '<div class="bsr__card-pricecol">' +
            '<span class="bsr__price">' + moneyFormat(p.p) + '</span>' +
            compareHtml +
          '</div>' +
          '<div class="bsr__card-ship">' +
            '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><rect x="2" y="6" width="13" height="11" rx="1"/><path d="M15 9h4l3 3v5h-7"/><circle cx="7" cy="19" r="2"/><circle cx="17" cy="19" r="2"/></svg>' +
            '<span><strong>FREE</strong> 2-day delivery</span>' +
          '</div>' +
          sizesHtml +
          atcHtml +
        '</div>' +
        '<div class="bsr__card-specs">' + specsHtml + '</div>' +
      '</article>'
    );
  }

  function renderResults(result, query) {
    var container = document.querySelector('[data-bsr-list]');
    var countEl = document.querySelector('#bsr-visible-count');
    var subEl = document.querySelector('.bsr__sub');
    if (!container) return;

    var main = (result && result.main) || [];
    var related = (result && result.related) || [];

    // Set the highlight token list before any cards render. Use the
    // ORIGINAL query tokens (not synonyms) so we only highlight what
    // the user actually typed.
    _highlightTokens = query ? tokenize(query) : [];

    // Header counts always reflect MAIN results only (so the sub-line
    // doesn't lie about how many strong matches we found).
    if (countEl) countEl.textContent = main.length + (main.length === 1 ? ' item' : ' items');
    if (subEl && query) {
      subEl.innerHTML = main.length + ' result' + (main.length === 1 ? '' : 's') +
        ' for <strong>"' + escapeHtml(query) + '"</strong>';
    }

    // Update hero chips (main count + related count, hide related chip if 0)
    var chipMainN = document.querySelector('[data-bsr-chip-main-n]');
    var chipMainS = document.querySelector('[data-bsr-chip-main-s]');
    if (chipMainN) chipMainN.textContent = main.length;
    if (chipMainS) chipMainS.textContent = main.length === 1 ? '' : 's';
    var chipRel = document.querySelector('[data-bsr-chip-rel]');
    var chipRelN = document.querySelector('[data-bsr-chip-rel-n]');
    if (chipRel) {
      if (related.length > 0) {
        chipRel.removeAttribute('hidden');
        if (chipRelN) chipRelN.textContent = related.length;
      } else {
        chipRel.setAttribute('hidden', '');
      }
    }

    // Remove any previously-rendered related rail (we re-render on every search)
    var oldRelated = document.querySelector('[data-bsr-related]');
    if (oldRelated) oldRelated.parentNode.removeChild(oldRelated);

    // ── No strong matches at all — soft landing instead of dead-end ─
    if (main.length === 0 && related.length === 0) {
      // Hide the sponsored video on the empty state — it's wasted real estate.
      var heroBrand = document.querySelector('[data-bsr-brand]');
      if (heroBrand) heroBrand.setAttribute('hidden', '');
      var heroLogo = document.querySelector('[data-bsr-hero-brand]');
      if (heroLogo) heroLogo.setAttribute('hidden', '');

      var suggestion = result && result.suggestion;
      var topSellers = (result && result.topSellers) || [];

      var html = '<div class="bsr__empty">' +
        '<div class="bsr__empty-icon"><svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg></div>' +
        '<h2 class="bsr__empty-h">No matches' + (query ? ' for "' + escapeHtml(query) + '"' : '') + '.</h2>';

      if (suggestion) {
        html += '<p class="bsr__empty-dym">' +
          'Did you mean <a class="bsr__empty-dym-link" href="/search?q=' + encodeURIComponent(suggestion) + '">' + escapeHtml(suggestion) + '</a>?' +
        '</p>';
      } else {
        html += '<p class="bsr__empty-sub">Try a different search or browse a category below.</p>';
      }

      html += '</div>';

      // Recently viewed — show first if the user has any. Highest signal
      // for a returning visitor staring at a "no results" page.
      var rv = (window.BeachAIRecent && window.BeachAIRecent.get) ? window.BeachAIRecent.get() : [];
      if (rv.length > 0) {
        html += '<section class="bsr__empty-recent">' +
          '<header class="bsr__empty-recent-head"><h3>Pick up where you left off</h3></header>' +
          '<div class="bsr__empty-recent-grid">' +
          rv.slice(0, 6).map(function (p) {
            var url = '/products/' + escapeHtml(p.handle);
            return '<a class="bsr__empty-recent-card" href="' + url + '">' +
              (p.img ? '<img src="' + escapeHtml(p.img) + '" alt="" loading="lazy">' : '<div class="bsr__empty-pop-noimg"></div>') +
              '<div class="bsr__empty-pop-meta">' +
                '<span class="bsr__empty-pop-vendor">' + escapeHtml(p.v || '') + '</span>' +
                '<span class="bsr__empty-pop-title">' + escapeHtml((p.t || '').slice(0, 60)) + '</span>' +
              '</div>' +
            '</a>';
          }).join('') +
          '</div></section>';
      }

      // Browse-by-category cards — pulled from CATEGORY_LABELS
      var CATEGORY_LABELS = {
        tv: 'Televisions',
        soundbar: 'Soundbars',
        headphone: 'Headphones',
        camera: 'Cameras',
        projector: 'Projectors',
        monitor: 'Monitors',
        home_theater: 'Home Theater',
      };
      var catKeys = Object.keys(CATEGORY_LABELS);
      html += '<section class="bsr__empty-cats">' +
        '<header class="bsr__empty-cats-head"><h3>Browse by category</h3></header>' +
        '<div class="bsr__empty-cats-grid">' +
        catKeys.map(function (k) {
          return '<a class="bsr__empty-cat" href="/search?q=' + encodeURIComponent(k) + '">' +
                 '<span class="bsr__empty-cat-name">' + CATEGORY_LABELS[k] + '</span>' +
                 '<span class="bsr__empty-cat-arrow">→</span>' +
                 '</a>';
        }).join('') +
        '</div></section>';

      // Popular this week — top sellers as a horizontal rail
      if (topSellers.length > 0) {
        html += '<section class="bsr__empty-popular">' +
          '<header class="bsr__empty-popular-head"><h3>Popular this week</h3>' +
          '<a class="bsr__empty-popular-all" href="/collections/all">See all →</a></header>' +
          '<div class="bsr__empty-popular-grid">' +
          topSellers.map(function (p) {
            var img = (p.i && p.i[0]) ? p.i[0] : '';
            return '<a class="bsr__empty-pop-card" href="' + escapeHtml(p.h ? '/products/' + p.h : '#') + '">' +
              (img ? '<img src="' + escapeHtml(img) + '" alt="" loading="lazy">' : '<div class="bsr__empty-pop-noimg"></div>') +
              '<div class="bsr__empty-pop-meta">' +
                '<span class="bsr__empty-pop-vendor">' + escapeHtml(p.v || '') + '</span>' +
                '<span class="bsr__empty-pop-title">' + escapeHtml((p.t || '').slice(0, 60)) + '</span>' +
                '<span class="bsr__empty-pop-price">' + moneyFormat((p.pr || 0)) + '</span>' +
              '</div>' +
            '</a>';
          }).join('') +
          '</div></section>';
      }

      container.innerHTML = html;
      return;
    }

    // ── Main results (or fall back to related if main is empty) ─
    if (main.length > 0) {
      // First card gets Top Pick treatment (only when there's a query and
      // we're confident about the top match — i.e. the query was non-empty).
      container.innerHTML = main.map(function (p, i) {
        return renderCard(p, { top: i === 0 && !!query });
      }).join('');
    } else {
      // No direct hits — promote related into the main slot but flag the user.
      container.innerHTML =
        '<div class="bsr__softempty">' +
          'No exact matches for <strong>"' + escapeHtml(query) + '"</strong>. ' +
          'Here are some related products:' +
        '</div>' +
        related.map(function (p) { return renderCard(p, {}); }).join('');
      return;
    }

    // ── Related rail below main results ──────────────────────
    if (related.length > 0) {
      var relatedHtml =
        '<section class="bsr__related" data-bsr-related>' +
          '<header class="bsr__related-head">' +
            '<h2 class="bsr__related-h">Other related products you might like</h2>' +
            '<p class="bsr__related-sub">Less direct matches for &ldquo;' + escapeHtml(query) + '&rdquo;</p>' +
          '</header>' +
          '<div class="bsr__related-grid">' +
            related.map(function (p) { return renderCard(p, {}); }).join('') +
          '</div>' +
        '</section>';
      container.insertAdjacentHTML('afterend', relatedHtml);
    }
  }

  /* ── Add-to-cart handler — delegated click on data-bsr-atc buttons.
        Posts to /cart/add.js (Shopify Ajax API) with the variant ID,
        gives quick visual feedback, dispatches cart:updated for the
        theme's cart drawer/icon to refresh. */
  function attachAtcHandler() {
    if (window.__bsrAtcBound) return;
    window.__bsrAtcBound = true;
    document.addEventListener('click', function (ev) {
      var btn = ev.target && ev.target.closest && ev.target.closest('[data-bsr-atc]');
      if (!btn) return;
      ev.preventDefault();
      ev.stopPropagation();

      if (btn.disabled || btn.classList.contains('is-loading')) return;
      var variantId = btn.getAttribute('data-variant');
      if (!variantId) return;

      var labelEl = btn.querySelector('.bsr__card-atc-label');
      var originalLabel = labelEl ? labelEl.textContent : '';
      btn.classList.add('is-loading');
      if (labelEl) labelEl.textContent = 'Adding…';

      fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 }),
      })
        .then(function (r) {
          if (!r.ok) return r.json().then(function (j) { throw new Error(j.description || j.message || 'Add failed'); });
          return r.json();
        })
        .then(function () {
          btn.classList.remove('is-loading');
          btn.classList.add('is-added');
          if (labelEl) labelEl.textContent = 'Added ✓';
          document.dispatchEvent(new CustomEvent('cart:updated'));
          document.dispatchEvent(new CustomEvent('cart:item-added'));
          setTimeout(function () {
            btn.classList.remove('is-added');
            if (labelEl) labelEl.textContent = originalLabel || 'Add to cart';
          }, 1800);
        })
        .catch(function (err) {
          btn.classList.remove('is-loading');
          btn.classList.add('is-error');
          if (labelEl) labelEl.textContent = 'Error';
          console.warn('[atc]', err);
          setTimeout(function () {
            btn.classList.remove('is-error');
            if (labelEl) labelEl.textContent = originalLabel || 'Add to cart';
          }, 2200);
        });
    });
  }

  /* ── Boot ─────────────────────────────────────────────────── */
  function getQuery() {
    var p = new URLSearchParams(window.location.search);
    return (p.get('q') || '').trim();
  }

  /* Inject a "Pick up where you left off" rail above the results when
     the user has recently viewed products AND there are search results
     to compare against. Skipped on the empty state (the rail there is
     handled by renderResults() in a different path). */
  function renderRecentRail() {
    if (!window.BeachAIRecent || !window.BeachAIRecent.get) return;
    var rv = window.BeachAIRecent.get();
    if (!rv.length) return;
    var chips = document.querySelector('[data-bsr-chips]');
    if (!chips) return;
    // Don't double-render
    if (document.querySelector('[data-bsr-rv-rail]')) return;

    var rail = document.createElement('div');
    rail.className = 'bsr__rv-rail';
    rail.setAttribute('data-bsr-rv-rail', '');
    rail.innerHTML =
      '<div class="bsr__rv-rail-head">' +
        '<h3>Pick up where you left off</h3>' +
        '<button type="button" class="bsr__rv-rail-clear" data-bsr-rv-clear>Clear</button>' +
      '</div>' +
      '<div class="bsr__rv-rail-strip">' +
      rv.slice(0, 8).map(function (p) {
        var url = '/products/' + escapeHtml(p.handle);
        var img = p.img ? '<img src="' + escapeHtml(p.img) + '" alt="" loading="lazy">' :
                          '<div style="width:96px;height:96px;background:#f5f5f7;border-radius:10px;"></div>';
        return '<a class="bsr__rv-rail-card" href="' + url + '" title="' + escapeHtml(p.t || '') + '">' +
          img +
          '<span class="bsr__rv-rail-card-name">' + escapeHtml((p.t || '').slice(0, 50)) + '</span>' +
        '</a>';
      }).join('') +
      '</div>';
    chips.parentNode.insertBefore(rail, chips);
    var clearBtn = rail.querySelector('[data-bsr-rv-clear]');
    if (clearBtn) {
      clearBtn.addEventListener('click', function () {
        if (window.BeachAIRecent && window.BeachAIRecent.clear) window.BeachAIRecent.clear();
        rail.parentNode.removeChild(rail);
      });
    }
  }

  /* Render the natural-language interpretation chip above results, and
     auto-apply any filters we successfully extracted. The chip lets the
     user clear the interpretation back to a literal search. */
  function renderInterpretation(parsed, query) {
    if (!parsed || !parsed.interpretation) return;
    if (!parsed.priceMin && !parsed.priceMax && !parsed.vendor) return;

    var chips = document.querySelector('[data-bsr-chips]');
    if (!chips) return;

    var existing = document.querySelector('[data-bsr-interp]');
    if (existing) existing.parentNode.removeChild(existing);

    var box = document.createElement('div');
    box.className = 'bsr__interp';
    box.setAttribute('data-bsr-interp', '');
    box.innerHTML =
      '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" aria-hidden="true">' +
        '<path d="M12 2v4M12 18v4M2 12h4M18 12h4M5 5l2.5 2.5M16.5 16.5L19 19M5 19l2.5-2.5M16.5 7.5L19 5"/>' +
      '</svg>' +
      '<span>I read your query as <strong>' + escapeHtml(parsed.interpretation) + '</strong></span>' +
      '<button type="button" class="bsr__interp-clear" data-bsr-interp-clear>Show literal "' + escapeHtml(query) + '" instead</button>';
    chips.parentNode.insertBefore(box, chips);

    // Auto-check vendor filter if detected
    if (parsed.vendor) {
      var label = document.querySelector('[data-bsr-filter="vendor"][value="' + parsed.vendor + '"]');
      if (label) {
        label.checked = true;
        label.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    // Auto-pick the closest price bucket
    if (parsed.priceMax) {
      var bucket = null;
      if (parsed.priceMax <= 200) bucket = '0-200';
      else if (parsed.priceMax <= 500) bucket = '200-500';
      else if (parsed.priceMax <= 1000) bucket = '500-1000';
      else if (parsed.priceMax <= 2000) bucket = '1000-2000';
      if (bucket) {
        var radio = document.querySelector('[data-bsr-price="' + bucket + '"]');
        if (radio) { radio.checked = true; radio.dispatchEvent(new Event('change', { bubbles: true })); }
      }
    }

    var clear = box.querySelector('[data-bsr-interp-clear]');
    if (clear) {
      clear.addEventListener('click', function () {
        // Strip query of price/brand modifiers, redirect to literal search
        // (which is the "raw" cleaned query without the interpretation hint)
        location.href = '/search?q=' + encodeURIComponent(parsed.clean);
      });
    }
  }

  function boot() {
    if (!document.querySelector('.bsr')) return;
    var q = getQuery();

    attachAtcHandler();

    var parsed = parseNaturalQuery(q);

    search(q).then(function (result) {
      renderResults(result, q);
      renderBrandPanel(result && result.brand, q);
      renderRecentRail();
      renderInterpretation(parsed, q);
      // Tell the existing filter logic the new cards exist.
      var mainCount = (result && result.main) ? result.main.length : 0;
      document.dispatchEvent(new CustomEvent('bsr:results-rendered', { detail: { count: mainCount } }));
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Expose globally so the header autocomplete (and anything else that
  // wants to query the index) can reuse this engine without rebuilding.
  window.BeachAISearch = {
    search: search,
    autocomplete: autocomplete,
    didYouMean: function (q) {
      return loadIndex().then(function (data) {
        return didYouMean(q, data.products || []);
      });
    },
  };
  window.__bsrSearch = search;
})();
